import type {
  Bank,
  Card,
  Deposit,
  DepositMethod,
  DepositMethodDisplay,
  PayPal,
  PaymentMethod,
  PaymentMethodDisplay,
  PaymentsAPIResponse,
  Venmo,
  Withdrawal
} from '@library/types/payments';
import {
  METHOD_AEROPAY,
  METHOD_CARD,
  METHOD_PAYPAL,
  METHOD_VENMO
} from '@library/constants/payments';
import { getPaymentsDateTime } from '@library/utils/dateTime';
import { transformName } from '@library/component-utils/filters';

const sortTransactions = (a: Deposit | Withdrawal, b: Deposit | Withdrawal) => {
  const dateA = getPaymentsDateTime(a.created).toMillis();
  const dateB = getPaymentsDateTime(b.created).toMillis();

  return dateB - dateA;
};

export const transformPayments = (
  response: PaymentsAPIResponse | undefined
): PaymentsAPIResponse | undefined => {
  if (!response) {
    return undefined;
  }

  const { deposits, withdrawals } = response;

  return {
    paginationInfo: {
      current_page: 1,
      total_pages: 1
    },
    user: response.user,
    deposits: deposits.sort(sortTransactions),
    withdrawals: withdrawals.sort(sortTransactions)
  };
};

export const getMethodInfo = (
  method: DepositMethod | PaymentMethod,
  methodDisplay: DepositMethodDisplay | PaymentMethodDisplay,
  card: Card,
  bank: Bank
): string => {
  let methodInfo: string = methodDisplay;
  switch (method) {
    case METHOD_AEROPAY:
      if (bank) {
        methodInfo = `${methodInfo} ${bank.lastDigits || ''}`;
      }
      break;
    case METHOD_CARD:
      if (card) {
        methodInfo = `${transformName(
          card.cardCategory
        )} ${methodInfo} ${transformName(card.cardType)} ${
          card.lastDigits || ''
        }`;
      }
      break;
    default:
  }
  return methodInfo;
};

export const getMethodInfoLines = (
  method: DepositMethod | PaymentMethod,
  methodDisplay: DepositMethodDisplay | PaymentMethodDisplay,
  card: Card,
  bank: Bank,
  paypal: PayPal,
  venmo: Venmo
): { methodLine1: string; methodLine2: string } => {
  const methodLines: { methodLine1: string; methodLine2: string } = {
    methodLine1: methodDisplay,
    methodLine2: ''
  };

  switch (method) {
    case METHOD_AEROPAY:
      if (bank) {
        methodLines.methodLine1 = `${methodLines.methodLine1} ${bank.lastDigits}`;
        methodLines.methodLine2 = bank.name;
      }
      break;
    case METHOD_CARD:
      if (card) {
        methodLines.methodLine1 = `${transformName(card.cardCategory)} ${
          methodLines.methodLine1
        } ${card.lastDigits}`;
        methodLines.methodLine2 = transformName(card.cardType);
      }
      break;
    case METHOD_PAYPAL:
      if (paypal) {
        const consumerId = paypal.consumerId ? `: ${paypal.consumerId}` : '';
        methodLines.methodLine1 = `${methodLines.methodLine1} ${consumerId}`;
        methodLines.methodLine2 = paypal.email || '';
      }
      break;
    case METHOD_VENMO:
      if (venmo) {
        methodLines.methodLine1 = `${methodLines.methodLine1} Venmo Line 1`;
        methodLines.methodLine2 = ' Venmo Line 2';
      }
      break;
    default:
  }
  return methodLines;
};
